import React, { useEffect, useMemo, useState } from "react";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import useIsMobile from "../../hooks/useIsMobile";
import styles from "./responsive.module.scss";

const STORAGE_PREFIX = "listView:";

// Wraps a list in a table/card view switch. Table is the default on desktop and
// cards on mobile, but an explicit choice is remembered per screen so a user who
// prefers one keeps it on that device.
const ResponsiveView = ({
    value = [],
    loading = false,
    loadingText = "Loading...",
    emptyMessage = "Nothing to show.",
    renderCard,
    children,
    rows = 20,
    storageKey,
    toolbarLeft,
    hideToggle = false,
    flush = false,
}) => {
    const isMobile = useIsMobile();
    const [choice, setChoice] = useState(() => (storageKey ? window.localStorage.getItem(STORAGE_PREFIX + storageKey) : null));
    const [first, setFirst] = useState(0);
    const [pageRows, setPageRows] = useState(rows);

    // Without a card renderer there is nothing to switch to, so stay on the table.
    const preferred = choice || (isMobile ? "cards" : "table");
    const view = renderCard ? preferred : "table";
    const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);

    // Fall back to the first page whenever the underlying data set changes, so a
    // filter that shrinks the list can't strand the user on an empty page.
    useEffect(() => {
        setFirst(0);
    }, [items.length]);

    const selectView = (next) => {
        setChoice(next);
        if (storageKey) window.localStorage.setItem(STORAGE_PREFIX + storageKey, next);
    };

    const toggle = hideToggle || !renderCard ? null : (
        <div className={styles.viewToolbar}>
            {toolbarLeft ? <div className={styles.toolbarLeft}>{toolbarLeft}</div> : <span />}
            <div className={styles.viewSwitch} role="group" aria-label="Change list layout">
                <button type="button" className={view === "table" ? styles.viewSwitchActive : ""} onClick={() => selectView("table")} aria-pressed={view === "table"}>
                    <i className="pi pi-list" />
                    <span>Table</span>
                </button>
                <button type="button" className={view === "cards" ? styles.viewSwitchActive : ""} onClick={() => selectView("cards")} aria-pressed={view === "cards"}>
                    <i className="pi pi-th-large" />
                    <span>Cards</span>
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <>
                {toggle}
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>{loadingText}</span>
                </div>
            </>
        );
    }

    if (view === "table") {
        return (
            <>
                {toggle}
                <div className={flush ? styles.tableWrapFlush : styles.tableWrap}>{children}</div>
            </>
        );
    }

    const paged = items.slice(first, first + pageRows);

    return (
        <>
            {toggle}
            {items.length === 0 ? (
                <div className={styles.emptyState}>{emptyMessage}</div>
            ) : (
                <>
                    <div className={styles.cardList}>{paged.map((row, i) => renderCard(row, first + i))}</div>
                    {items.length > pageRows && (
                        <Paginator
                            first={first}
                            rows={pageRows}
                            totalRecords={items.length}
                            rowsPerPageOptions={[10, 20, 50]}
                            onPageChange={(e) => {
                                setFirst(e.first);
                                setPageRows(e.rows);
                            }}
                            className={styles.cardPaginator}
                            template="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                            currentPageReportTemplate="{first}-{last} of {totalRecords}"
                        />
                    )}
                </>
            )}
        </>
    );
};

export default ResponsiveView;
